/* Korrektheits-Prüfungen für Maps: Punkte abseits des Rasters, Entity-Verknüpfungen, deckungsgleiche Flächen (Z-Fighting), Lichter. Ohne DOM. */
import type { Brush, CheckFix, CheckItem, Doc, Entity, Vec3 } from './types.ts';
import I18n from '../i18n.ts';
import { faceNormal } from './brush.ts';
import { cross, dot, norm } from './vec.ts';
import { SPECIAL } from './perf.ts';
import { validBrush } from './simplify.ts';
import { COMPILER_FGDS, FGD_KEY, fgdNames } from './modprofile.ts';

const _ = I18n.t;

/** Auswahl für Entities: Brush-Entities über ihre Brushes, Punkt-Entities über die Entity-ID */
export const selOfEnts = (ents: Entity[]): { b: string[]; e: string[] } => ({
  b: ents.flatMap((e) => e.brushes.map((b) => b.id)),
  e: ents.filter((e) => !e.brushes.length).map((e) => e.id)
});

/** Brushes mit Eckpunkten abseits des ganzzahligen Rasters (häufige Ursache für Leaks und „plane not on grid“) */
export function offGridBrushes(doc: Doc): string[] {
  const out: string[] = [];
  doc.ents.forEach((e) => e.brushes.forEach((b) => { if (b.verts.some((v) => v.some((x) => Math.abs(x - Math.round(x)) > 0.001))) out.push(b.id); }));
  return out;
}

/** Brushes, die keine gültige konvexe Form haben: Flächen nicht eben (ein Eckpunkt verschoben), Form nach innen gewölbt (konkav) oder Kanten ohne Gegenfläche. HLCSG bricht mit „Illegal Brush“ ab oder überspringt sie. */
export function invalidBrushes(doc: Doc): string[] {
  const out: string[] = [];
  doc.ents.forEach((e) => e.brushes.forEach((b) => { if (!validBrush(b)) out.push(b.id); }));
  return out;
}

/** Schlüssel, die auf targetname-Werte zeigen. multi_manager nutzt seine Schlüssel selbst als Namen und wird nicht geprüft. */
const LINK_KEYS = ['target', 'killtarget'];
/** Entities, die ohne target nichts auslösen */
const NEEDS_TARGET = /^(trigger_once|trigger_multiple|trigger_relay|trigger_counter|func_button)$/;

export function linkIssues(doc: Doc): { dangling: Entity[]; deadTriggers: Entity[] } {
  const names = new Set<string>();
  doc.ents.forEach((e) => { if (e.kv.targetname) names.add(e.kv.targetname); });
  const dangling: Entity[] = [], deadTriggers: Entity[] = [];
  doc.ents.forEach((e, i) => {
    if (i === 0 || e.kv.classname === 'multi_manager') return;
    if (LINK_KEYS.some((k) => e.kv[k] && e.kv[k].charAt(0) !== '!' && !names.has(e.kv[k]))) dangling.push(e);
    if (NEEDS_TARGET.test(e.kv.classname || '') && !e.kv.target && !e.kv.master) deadTriggers.push(e);
  });
  return { dangling, deadTriggers };
}

type P2 = [number, number];
/** Überlappen sich zwei konvexe Polygone mit mehr als tol Tiefe? (Trennachsen-Test) */
function overlap2(a: P2[], b: P2[], tol: number): boolean {
  for (const poly of [a, b]) {
    for (let i = 0; i < poly.length; i++) {
      const p = poly[i], q = poly[(i + 1) % poly.length];
      const ax: P2 = [-(q[1] - p[1]), q[0] - p[0]], len = Math.hypot(ax[0], ax[1]);
      if (len < 1e-9) continue;
      const proj = (pts: P2[]) => { const v = pts.map((x) => (x[0] * ax[0] + x[1] * ax[1]) / len); return [Math.min(...v), Math.max(...v)]; };
      const pa = proj(a), pb = proj(b);
      if (Math.min(pa[1], pb[1]) - Math.max(pa[0], pb[0]) <= tol) return false;
    }
  }
  return true;
}

/** Fläche des Schnitts zweier konvexer Polygone (Sutherland-Hodgman) samt Polygon; beide mit gleicher Umlaufrichtung (gegen den Uhrzeigersinn) */
function clip2(subject: P2[], clip: P2[]): P2[] {
  let out = subject;
  for (let i = 0; i < clip.length && out.length; i++) {
    const a = clip[i], b = clip[(i + 1) % clip.length], inp = out; out = [];
    const side = (p: P2) => (b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]);
    for (let k = 0; k < inp.length; k++) {
      const p = inp[k], q = inp[(k + 1) % inp.length], sp = side(p), sq = side(q);
      if (sp >= 0) out.push(p);
      if ((sp >= 0) !== (sq >= 0)) { const t = sp / (sp - sq); out.push([p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t]); }
    }
  }
  return out;
}
const area2 = (p: P2[]): number => { let a = 0; for (let i = 0; i < p.length; i++) { const q = p[(i + 1) % p.length]; a += p[i][0] * q[1] - q[0] * p[i][1]; } return a / 2; };

/** Überlagerung zweier Flächen in derselben Ebene und Richtung: Brushes a und b, ihre Flächenindizes, die gemeinsame Fläche im Raum und ob eine Fläche vollständig von der anderen verdeckt wird */
export interface Overlap { a: string; b: string; fa: number; fb: number; poly: Vec3[]; area: number; coverA: boolean; coverB: boolean }

/** Alle Überlagerungen von Flächen mit gleicher Ebene und Richtung (flackern im Spiel). skip: schon anders gemeldete Brushes. anyLook: auch bei gleicher Textur und Ausrichtung melden. */
export function zFightOverlaps(doc: Doc, skip?: Set<string>, anyLook = false): Overlap[] {
  interface F { brush: string; face: number; poly: Vec3[]; look: string }
  const groups = new Map<string, F[]>();
  doc.ents.forEach((e) => e.brushes.forEach((b) => {
    if (skip && skip.has(b.id)) return;
    b.faces.forEach((f, fi) => {
      if (SPECIAL.test(f.tex) || f.tex.charAt(0) === '!') return; // Wasser grenzt absichtlich bündig an Gelände
      const n = faceNormal(b.verts, f.vi), poly = f.vi.map((k) => b.verts[k]);
      const key = n.map((x) => x.toFixed(3)).join(',') + '|' + dot(n, poly[0]).toFixed(1);
      const l = groups.get(key); const item = { brush: b.id, face: fi, poly, look: [f.tex.toUpperCase(), f.ua, f.uo, f.va, f.vo, f.rot, f.xs, f.ys].join('|') };
      if (l) l.push(item); else groups.set(key, [item]);
    });
  }));
  const out: Overlap[] = [];
  groups.forEach((list, key) => {
    if (list.length < 2) return;
    const n = key.split('|')[0].split(',').map(Number) as Vec3;
    const helper: Vec3 = Math.abs(n[2]) < 0.9 ? [0, 0, 1] : [1, 0, 0];
    const u = norm(cross(n, helper)), v = cross(n, u);
    const d0 = dot(n, list[0].poly[0]);
    const flat = list.map((f) => { const q = f.poly.map((p): P2 => [dot(p, u), dot(p, v)]); return area2(q) < 0 ? q.reverse() : q; });
    for (let i = 0; i < list.length; i++) for (let j = i + 1; j < list.length; j++) {
      if (list[i].brush === list[j].brush || (!anyLook && list[i].look === list[j].look)) continue; // gleiche Textur mit gleicher Ausrichtung flackert nicht sichtbar (anyLook: trotzdem melden, z. B. vor dem Ausrichten)
      if (!overlap2(flat[i], flat[j], 0.5)) continue;
      const both = clip2(flat[i], flat[j]), ar = Math.abs(area2(both));
      const ai = Math.abs(area2(flat[i])), aj = Math.abs(area2(flat[j]));
      out.push({
        a: list[i].brush, b: list[j].brush, fa: list[i].face, fb: list[j].face, area: ar,
        poly: both.map((q): Vec3 => [0, 1, 2].map((k) => Math.round((u[k] * q[0] + v[k] * q[1] + n[k] * d0) * 100) / 100 + 0) as Vec3),
        coverA: ai > 0 && ar >= 0.98 * ai, coverB: aj > 0 && ar >= 0.98 * aj
      });
    }
  });
  return out;
}

/** Brushes, deren Flächen mit gleicher Ebene und Richtung eine Fläche eines anderen Brushes überdecken (flackern im Spiel). skip: schon anders gemeldete Brushes */
export function zFightBrushes(doc: Doc, skip?: Set<string>, anyLook = false): string[] {
  const hit = new Set<string>();
  zFightOverlaps(doc, skip, anyLook).forEach((o) => { hit.add(o.a); hit.add(o.b); });
  return [...hit];
}

/** Verzerrung einer Textur auf einer Fläche: Streckung entlang u und v (1 = unverzerrt, relativ zur eingestellten Skalierung) und Scherung (|cos| des Winkels zwischen den projizierten Achsen) */
export function texDistortion(normal: Vec3, f: { ua: Vec3; va: Vec3; xs: number; ys: number }): { u: number; v: number; shear: number } {
  const proj = (a: Vec3): Vec3 => { const d = dot(a, normal); return [a[0] - normal[0] * d, a[1] - normal[1] * d, a[2] - normal[2] * d]; };
  const pu = proj(f.ua), pv = proj(f.va), lu = Math.hypot(...pu), lv = Math.hypot(...pv), au = Math.hypot(...f.ua) || 1, av = Math.hypot(...f.va) || 1;
  if (lu < 1e-6 || lv < 1e-6) return { u: Infinity, v: Infinity, shear: 1 };
  return { u: au / lu, v: av / lv, shear: Math.abs(dot(pu, pv)) / (lu * lv) };
}

/** Texturen, die auf einer Fläche stark gestreckt oder verschert liegen (Achsen stehen schief zur Fläche, z. B. senkrechte Texturachsen auf einer Schräge) */
export function texDistortBrushes(doc: Doc, maxStretch = 1.5, maxShear = 0.3): { brushes: string[]; faces: number } {
  const hit = new Set<string>(); let faces = 0;
  doc.ents.forEach((e) => e.brushes.forEach((b) => {
    const n = distortedFaces(b, maxStretch, maxShear).length;
    if (n) { hit.add(b.id); faces += n; }
  }));
  return { brushes: [...hit], faces };
}
/** Indizes der Flächen eines Brushes mit gestreckter oder verscherter Textur */
export function distortedFaces(b: Brush, maxStretch = 1.5, maxShear = 0.3): number[] {
  const out: number[] = [];
  b.faces.forEach((f, i) => {
    if (SPECIAL.test(f.tex)) return;
    const d = texDistortion(faceNormal(b.verts, f.vi), f);
    if (d.u > maxStretch || d.v > maxStretch || d.shear > maxShear) out.push(i);
  });
  return out;
}

export const HINT = /^hint$/i, SKIP = /^skip$/i;
/** Brush mit HINT-Fläche, dessen übrige Flächen nicht alle HINT oder SKIP sind: csg bricht mit „mixed face contents“ ab (gemessen, docs/optimierung-vorgaben.md) */
export const isHintMixed = (b: Brush): boolean => b.faces.some((f) => HINT.test(f.tex)) && b.faces.some((f) => !HINT.test(f.tex) && !SKIP.test(f.tex));
export function hintMixedBrushes(doc: Doc): string[] {
  const out: string[] = [];
  doc.ents.forEach((e) => e.brushes.forEach((b) => { if (isHintMixed(b)) out.push(b.id); }));
  return out;
}
/** Hint-Brushes in Brush-Entities: ohne Wirkung auf VIS (func_wall) bzw. nur auf die BSP-Leafs (func_detail); sie gehören in die Welt (gemessen) */
export function hintInEntityBrushes(doc: Doc): string[] {
  const out: string[] = [];
  doc.ents.forEach((e, i) => { if (i > 0) e.brushes.forEach((b) => { if (b.faces.some((f) => HINT.test(f.tex))) out.push(b.id); }); });
  return out;
}

/** Lichter mit Helligkeit 0 oder weniger (_light mit vier Werten) */
export function darkLights(doc: Doc): Entity[] {
  return doc.ents.filter((e) => {
    if (!/^light(_spot)?$/.test(e.kv.classname || '') || !e.kv._light) return false;
    const n = e.kv._light.trim().split(/\s+/).map(Number);
    return n.length >= 4 && n.every((x) => Number.isFinite(x)) && n[3] <= 0;
  });
}

/** Höchstzahl der Markierungen je Befund (größte zuerst), damit Berichte und Ansicht handlich bleiben */
export const MAX_MARKS = 400;

/** Texturgröße je Name aus den geladenen WADs (null: unbekannt) */
export type TexSize = (name: string) => { w: number; h: number } | null;
const isPow2 = (n: number): boolean => n > 0 && (n & (n - 1)) === 0;

/** Texturen, deren Breite oder Höhe keine Zweierpotenz ist (OpenGL verkleinert sie auf die nächste Zweierpotenz, sie wirken gestreckt), samt der Brushes, die sie nutzen */
export function npotTextures(doc: Doc, size: TexSize): { tex: { name: string; w: number; h: number }[]; brushes: string[] } {
  const bad = new Map<string, { name: string; w: number; h: number }>(), brushes: string[] = [];
  doc.ents.forEach((e) => e.brushes.forEach((b) => {
    let hit = false;
    b.faces.forEach((f) => {
      const k = f.tex.toUpperCase();
      if (SPECIAL.test(k)) return;
      if (!bad.has(k)) { const s = size(k); if (s && (!isPow2(s.w) || !isPow2(s.h))) bad.set(k, { name: k, w: s.w, h: s.h }); }
      if (bad.has(k)) hit = true;
    });
    if (hit) brushes.push(b.id);
  }));
  return { tex: [...bad.values()], brushes };
}

export function lintItems(doc: Doc, hidden: string[], texSize?: TexSize): CheckItem[] {
  const out: CheckItem[] = [];
  const add = (id: string, text: string, sel?: { b: string[]; e: string[] }, fix?: CheckFix, level: 'info' | 'warn' | 'err' = 'warn', marks?: Vec3[][]) => {
    const it: CheckItem = { level, id, text };
    if (marks && marks.length) it.marks = marks;
    if (sel && (sel.b.length || sel.e.length)) it.sel = sel;
    if (it.sel && fix) it.fix = fix;
    out.push(it);
  };
  const inv = invalidBrushes(doc);
  if (inv.length) add('lint.invalid_brush', _('lint.invalid_brush', { count: inv.length }), { b: inv, e: [] }, 'hull');
  // Karte mit eigenem Mod-Profil ohne die FGD der Compiler-Werkzeuge: sie vorschlagen (ohne Profil gilt sie ohnehin als Vorgabe)
  const world = doc.ents[0], profile = world ? fgdNames(world.kv[FGD_KEY]) : [];
  if (world && profile.length && !profile.some((n) => COMPILER_FGDS.includes(n.toLowerCase()))) add('lint.no_compiler_fgd', _('lint.no_compiler_fgd', { file: COMPILER_FGDS[0] }), { b: [], e: [world.id] }, 'fgd', 'info');
  const grid = offGridBrushes(doc);
  if (grid.length) add('lint.off_grid', _('lint.off_grid', { count: grid.length }), { b: grid, e: [] }, 'snap');
  const hm = hintMixedBrushes(doc);
  if (hm.length) add('lint.hint_mixed', _('lint.hint_mixed', { count: hm.length }), { b: hm, e: [] }, 'skip', 'err');
  const he = hintInEntityBrushes(doc);
  if (he.length) add('lint.hint_entity', _('lint.hint_entity', { count: he.length }), { b: he, e: [] });
  const li = linkIssues(doc);
  if (li.dangling.length) add('lint.dangling', _('lint.dangling', { count: li.dangling.length, names: [...new Set(li.dangling.map((e) => e.kv.target || e.kv.killtarget))].slice(0, 4).join(', ') }), selOfEnts(li.dangling));
  if (li.deadTriggers.length) add('lint.dead_trigger', _('lint.dead_trigger', { count: li.deadTriggers.length }), selOfEnts(li.deadTriggers));
  const zo = zFightOverlaps(doc, new Set(hidden)), zf = [...new Set(zo.flatMap((o) => [o.a, o.b]))];
  if (zf.length) add('lint.zfight', _('lint.zfight', { count: zf.length }), { b: zf, e: [] }, 'zfight', 'warn', zo.slice().sort((x, y) => y.area - x.area).slice(0, MAX_MARKS).map((o) => o.poly));
  const td = texDistortBrushes(doc);
  if (td.brushes.length) add('lint.tex_distort', _('lint.tex_distort', { count: td.brushes.length, faces: td.faces }), { b: td.brushes, e: [] }, 'texalign');
  if (texSize) {
    const np = npotTextures(doc, texSize);
    if (np.tex.length) add('lint.npot_texture', _('lint.npot_texture', { count: np.tex.length, names: np.tex.slice(0, 4).map((t) => t.name + ' ' + t.w + '×' + t.h).join(', ') + (np.tex.length > 4 ? ' …' : '') }), { b: np.brushes, e: [] }, undefined, 'info');
  }
  const dark = darkLights(doc);
  if (dark.length) add('lint.dark_light', _('lint.dark_light', { count: dark.length }), selOfEnts(dark), 'delete');
  const sky = doc.ents.some((e) => e.brushes.some((b) => b.faces.some((f) => /^sky$/i.test(f.tex))));
  if (sky && !doc.ents.some((e) => e.kv.classname === 'light_environment')) add('lint.no_env_light', _('lint.no_env_light'));
  return out;
}
